import { getRequestOptions, postRequestOptions, getAxiosConfig, getAxiosConfigWithContentType } from '../helpers/Fetchwrapper';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export const InvoiceService = {
    /**
     * Get invoices list with filtering and pagination
     */
    getInvoicesList: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams({
                offset: params.offset || 0,
                limit: params.limit || 10,
                orderBy: params.orderBy || 'entity_id',
                orderType: params.orderType || 'desc',
                globalSearch: params.globalSearch || '',
                ...params
            }).toString();

            const response = await fetch(
                `${API_URL}invoices/list?${queryParams}`,
                getRequestOptions()
            );
            return await response.json();
        } catch (error) {
            console.error('Error fetching invoices:', error);
            throw error;
        }
    },

    /**
     * Get invoice details by ID
     */
    getInvoiceDetails: async (invoiceId) => {
        try {
            const response = await fetch(
                `${API_URL}invoice/details/${invoiceId}`,
                getRequestOptions()
            );
            return await response.json();
        } catch (error) {
            console.error('Error fetching invoice details:', error);
            throw error;
        }
    },

    /**
     * Get order details for invoice creation
     */
    getOrderForInvoice: async (orderId) => {
        try {
            const response = await fetch(
                `${API_URL}invoices/order/${orderId}`,
                getRequestOptions()
            );
            return await response.json();
        } catch (error) {
            console.error('Error fetching order for invoice:', error);
            throw error;
        }
    },

    /**
     * Create new invoice
     */
    createInvoice: async (invoiceData) => {
        try {
            const response = await fetch(
                `${API_URL}invoices/create`,
                postRequestOptions(invoiceData)
            );
            return await response.json();
        } catch (error) {
            console.error('Error creating invoice:', error);
            throw error;
        }
    },

    /**
     * Print invoice
     */
    printInvoice: async (invoiceId) => {
        try {
            const response = await fetch(
                `${API_URL}invoices/print/${invoiceId}`,
                getRequestOptions()
            );
            
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `invoice-${invoiceId}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                return { success: true };
            } else {
                throw new Error('Failed to generate PDF');
            }
        } catch (error) {
            console.error('Error printing invoice:', error);
            throw error;
        }
    },

    /**
     * Calculate invoice totals
     */
    calculateInvoiceTotals: (items) => {
        let subtotal = 0;
        let totalTax = 0;
        let totalDiscount = 0;
        let totalQty = 0;

        items.forEach(item => {
            const itemSubtotal = (item.price || 0) * (item.qtyInvoice || 0);
            const itemTax = (item.taxAmount || 0) * (item.qtyInvoice || 0);
            const itemDiscount = (item.discountAmount || 0) * (item.qtyInvoice || 0);

            subtotal += itemSubtotal;
            totalTax += itemTax;
            totalDiscount += itemDiscount;
            totalQty += item.qtyInvoice || 0;
        });

        return {
            subtotal,
            totalTax,
            totalDiscount,
            totalQty,
            grandTotal: subtotal + totalTax - totalDiscount
        };
    },

    /**
     * Validate invoice items
     */
    validateInvoiceItems: (items) => {
        const errors = [];

        items.forEach((item, index) => {
            if (!item.qtyInvoice || item.qtyInvoice <= 0) {
                errors.push(`Item ${index + 1}: Quantity to invoice is required and must be greater than 0`);
            }

            if (item.qtyInvoice > item.maxQtyInvoice) {
                errors.push(`Item ${index + 1}: Quantity to invoice cannot exceed ${item.maxQtyInvoice}`);
            }

            if (!item.price || item.price <= 0) {
                errors.push(`Item ${index + 1}: Price is required and must be greater than 0`);
            }
        });

        return errors;
    },

    /**
     * Get invoice status options
     */
    getInvoiceStatusOptions: () => {
        return [
            { value: 'draft', label: 'Draft', color: 'secondary' },
            { value: 'pending', label: 'Pending', color: 'warning' },
            { value: 'paid', label: 'Paid', color: 'success' },
            { value: 'partially_paid', label: 'Partially Paid', color: 'info' },
            { value: 'overdue', label: 'Overdue', color: 'danger' },
            { value: 'canceled', label: 'Canceled', color: 'dark' }
        ];
    },

    /**
     * Format invoice status for display
     */
    formatInvoiceStatus: (status) => {
        const statusOptions = InvoiceService.getInvoiceStatusOptions();
        const statusOption = statusOptions.find(option => option.value === status);
        return statusOption || { value: status, label: status, color: 'secondary' };
    },

    /**
     * Format currency
     */
    formatCurrency: (amount) => {
        return new Intl.NumberFormat('en-BD', {
            style: 'currency',
            currency: 'BDT',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(amount || 0);
    },

    /**
     * Format date
     */
    formatDate: (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    },

    /**
     * Format date for display (short format)
     */
    formatDateShort: (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    },

    /**
     * Generate next invoice number (client-side preview)
     */
    generateInvoiceNumber: (lastInvoiceNumber) => {
        if (!lastInvoiceNumber) {
            return 'INV-00000001';
        }

        const numberPart = lastInvoiceNumber.substring(4);
        const nextNumber = parseInt(numberPart) + 1;
        return 'INV-' + nextNumber.toString().padStart(8, '0');
    },

    /**
     * Calculate payment status
     */
    calculatePaymentStatus: (invoice) => {
        const totalAmount = invoice.amount || 0;
        const totalPaid = invoice.totalPaid || 0;

        if (totalPaid === 0) {
            return 'unpaid';
        } else if (totalPaid >= totalAmount) {
            return 'paid';
        } else {
            return 'partially_paid';
        }
    },

    /**
     * Get payment status badge class
     */
    getPaymentStatusBadge: (status) => {
        switch (status) {
            case 'paid':
                return 'badge-success';
            case 'partially_paid':
                return 'badge-warning';
            case 'unpaid':
                return 'badge-danger';
            default:
                return 'badge-secondary';
        }
    }
};

export default InvoiceService; 